import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import CustomCkeditor from "../../common/customeditor";
import * as staffService from "../../services/subadmin.services";
import * as roleService from "../../services/role.services";
import Breadcrums from "../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import {
  ATLEAST_ONE_CAPITAL_REGEX,
  ATLEAST_ONE_NUMBER_REGEX,
  ATLEAST_ONE_SMALL_REGEX,
  ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  SWAL_SETTINGS,
} from "../../../../utils/Constants";
import {
  capitalizeFirstLetter,
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import * as Yup from "yup";
import CustomError from "../../../../utils/customError";

const SubAdminAdd = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("label_sub_admin"), url: "/admin/staff/list/1" },
    { title: t("link_add"), url: "" },
  ];
  const [newshow, setNewShow] = useState("false");
  const [conformshow, setConformShow] = useState("false");
  const [previewimage, setPreviewImage] = useState("");
  const [roleData, setRoleData] = useState([]);

  const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png", "image/gif", "image/webp"];


  useEffect(() => {
    staffService.RoleOptions().then((response) => {
      setRoleData(response?.data || []);
    }).catch((error) => {
      console.log("error=====>", error);
    });
  }, []);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email address').required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .matches(
        ATLEAST_ONE_SMALL_REGEX,
        "Password should have at least 1 lowercase letter"
      )
      .matches(
        ATLEAST_ONE_CAPITAL_REGEX,
        "Password should have at least 1 capital letter"
      )
      .matches(
        ATLEAST_ONE_NUMBER_REGEX,
        "Password should have at least 1 number"
      )
      .matches(
        ATLEAST_ONE_SPECIAL_CHARACTER_REGEX,
        "Password should have at least 1 special character"
      )
      .min(PASSWORD_MIN_LENGTH, "Password should be at least 8 characters long")
      .max(
        PASSWORD_MAX_LENGTH,
        "Password cannot be more than 20 characters long"
      ),
    confirm_password: Yup.string().required("Confirm password is required").oneOf([Yup.ref("password"), null], "Passwords don't match"),
    permission_role: Yup.string().required("Role is required"),
    files: Yup.mixed()
      .test("fileType", "Only image files are allowed", (value) => {
        if (!value) return true;
        return SUPPORTED_FORMATS.includes(value.type);
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      confirm_password: '',
      permission_role: '',
      files: ''
    },
    validateOnBlur: false,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      globalLoader(true);
      setSubmitted(true);
      let formData = new FormData();
      formData.append('profile_image', values.files);
      formData.append('first_name', values.first_name);
      formData.append('last_name', values.last_name);
      formData.append('email', values.email);
      formData.append('password', values.password);
      formData.append('confirm_password', values.confirm_password);
      formData.append('permission_role', values.permission_role);
      staffService.Add(formData).then((response) => {
        setSubmitting(false);
        if (response.success) {
          Swal.fire({
            icon: "success",
            text: response.message,
            ...SWAL_SETTINGS,
          });
          globalLoader(false);
          resetForm({ values: "" });
          navigate(`/admin/staff/list/1`);
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
          setSubmitted(false);
          globalLoader(false);
        }
      })
        .catch((error) => {
          console.log("error ====> ", error);
        });
    },
  });

  const handleshow = (event) => {
    if (event === "newpwd") {
      setNewShow(!newshow);
    }
    if (event === "confirepwd") {
      setConformShow(!conformshow);
    }
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <form onSubmit={formik.handleSubmit}>
        <div className="row row-sm">
          <div className="col-lg-12 col-md-12 animation_fade">
            <div className="card custom-card">
              <div className="card-body">
                <div>
                  <h6 className="main-content-label mb-3">{t("link_add")} Sub Admin</h6>
                </div>
                <div className="row row-sm">
                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="first_name" className="text-left d-flex">
                      {t("First Name")}
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input name="first_name" id="first_name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.first_name} className="form-control" />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="first_name" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="last_name" className="text-left d-flex">
                      {t("Last Name")}
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input name="last_name" id="last_name" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.last_name} className="form-control" />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="last_name" form={formik} />
                    </span>
                  </div>

                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="email" className="text-left d-flex">
                      {t("Email")}
                      <span className="requirestar">*</span>{" "}
                    </label>
                    <input name="email" id="email" type="text" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.email} className="form-control" />
                    <span className="text-danger d-flex text-left">
                      <CustomError name="email" form={formik} />
                    </span>
                  </div>
                  <div className='col-md-12 text-center form-group'>
                    <label htmlFor='title' className='text-left d-flex'>Password:<span className="requirestar">*</span></label>
                    <div className="input-group cp">
                      <input
                        name='password'
                        id='password'
                        type={!newshow === true ? "text" : "password"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.password}
                        className='form-control' />
                      <span
                        className="input-group-text"
                        onClick={() => {
                          handleshow("newpwd");
                        }}
                      >
                        {!newshow === true ? (
                          <i className="far fa-eye-slash"></i>
                        ) : (
                          <i className="far fa-eye"></i>
                        )}
                      </span>
                    </div>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="password" form={formik} />
                    </span>
                  </div>
                  <div className="col-lg-12 text-center form-group">
                    <label htmlFor='title' className='text-left d-flex'>Confirm Password:<span className="requirestar">*</span> </label>
                    <div className="input-group cp">
                      <input
                        name='confirm_password'
                        id='confirm_password'
                        type={!conformshow === true ? "text" : "password"}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.confirm_password}
                        className='form-control'
                      />
                      <span
                        className="input-group-text"
                        onClick={() => {
                          handleshow("confirepwd");
                        }}
                      >
                        {!conformshow === true ? (
                          <i className="far fa-eye-slash"></i>
                        ) : (
                          <i className="far fa-eye"></i>
                        )}
                      </span>
                    </div>
                    <span className="text-danger d-flex text-left">
                      <CustomError name="confirm_password" form={formik} />
                    </span>
                  </div>
                  <div className="col-md-12 text-center form-group">
                    <label htmlFor="permission_role" className="text-left d-flex">
                      Role:
                      <span className="requirestar">*</span>
                    </label>
                    <div className="select-down-arrow">
                      <select name="permission_role" id="permission_role" className="form-control" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.permission_role || ""}>
                        <option value="" disabled>
                          {t("Select Role")}
                        </option>
                        {roleData.map((item) => (
                          <option key={item._id} value={item._id}>
                            {capitalizeFirstLetter(item.title)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {formik.touched.permission_role && formik.errors.permission_role && <span className="text-danger d-flex text-left">{formik.errors.permission_role}</span>}
                  </div>
                   <div className='col-lg-12 text-center form-group'>
                        <label htmlFor='files' className='text-left d-flex'>Profile:</label>
                        <input className='form-control imgInput' id="files" name="files" accept="image/*" type="file" onChange={(event) => {
                          formik.setFieldValue("files", event.currentTarget.files[0] || "");
                          if (event.currentTarget.files.length === 1) {
                            setPreviewImage(URL.createObjectURL(event.currentTarget.files[0]));
                          } else {
                            setPreviewImage("");
                          }
                        }}
                        />
                       <span className="text-danger d-flex text-left">
                      <CustomError name="files" form={formik} />
                    </span>
                      </div>
                      {
                        previewimage ?
                          <div className='mb-4'>
                            <img src={previewimage} style={{ height: "100px" }} alt={""} />
                          </div> : ""
                      }
                  <div className="mt-5">
                    <button className="btn btn-info mr-2" type="submit" disabled={submitted ? true : null}>
                      <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                      {t("btn_submit")}
                    </button>
                    <button className="btn ripple btn-secondary" type="button" disabled={submitted ? true : null} onClick={() => navigate(-1)}>
                      <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                      {t("btn_cancel")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default SubAdminAdd;
